/**
 * InterviewErrorAlert
 *
 * Displays interview errors with contextual recovery actions.
 */

import { Alert, Button, Group, Stack, Text } from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { InterviewErrorCode } from '@/lib/errors/interview-errors';

export interface InterviewErrorAlertProps {
  error: string;
  errorCode: InterviewErrorCode | null;
  canStartInterview: boolean;
  onRetry: () => void;
  onRefreshPage: () => void;
  onStartNewInterview: () => void;
  onClearError: () => void;
}

export function InterviewErrorAlert({
  error,
  errorCode,
  canStartInterview,
  onRetry,
  onRefreshPage,
  onStartNewInterview,
  onClearError,
}: Readonly<InterviewErrorAlertProps>) {
  const showStartNewButton =
    (error.includes('Call ended') || error.includes('Technical error')) &&
    canStartInterview;

  return (
    <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red">
      <Stack gap="xs">
        <Text>{error}</Text>

        {showStartNewButton && (
          <Button size="sm" onClick={onStartNewInterview} variant="light">
            Start New Interview
          </Button>
        )}

        {errorCode && (
          <Group gap="xs">
            {errorCode === InterviewErrorCode.NETWORK_TIMEOUT && (
              <Button size="sm" onClick={onRetry} variant="light">
                Check connection and retry
              </Button>
            )}

            {errorCode === InterviewErrorCode.VOICE_CONNECTION_FAILED && (
              <Button size="sm" onClick={onRetry} variant="light">
                Try again
              </Button>
            )}

            {(errorCode === InterviewErrorCode.SESSION_START_FAILED ||
              errorCode === InterviewErrorCode.SESSION_NOT_FOUND) && (
              <Button size="sm" onClick={onRefreshPage} variant="light">
                Refresh page
              </Button>
            )}

            {errorCode === InterviewErrorCode.INTERVIEW_LIMIT_REACHED && (
              <Button size="sm" onClick={onClearError} variant="light">
                OK
              </Button>
            )}

            {(errorCode === InterviewErrorCode.USER_NOT_AUTHENTICATED ||
              errorCode === InterviewErrorCode.UNAUTHORIZED) && (
              <Button size="sm" onClick={onRefreshPage} variant="light">
                Please log in
              </Button>
            )}
          </Group>
        )}
      </Stack>
    </Alert>
  );
}
